<template>
  <div class="inl-page">
    <!-- Hero -->
    <div class="hero">
      <div class="hero-txt">
        <div class="hero-eyebrow">PORTAL HR</div>
        <h1 class="hero-title">Solicitări de înlocuire</h1>
        <p class="hero-sub">Cererile în care ești desemnat înlocuitor. Concediile se confirmă ca să meargă la superior; tichetele sunt afișate spre informare.</p>
      </div>
      <div class="co-chip">
        <v-icon size="18" class="mr-1">mdi-account-switch</v-icon>
        <span class="co-val">{{ cereri.length + tichete.length }}</span>
        <span class="co-lbl">în așteptare</span>
      </div>
    </div>

    <!-- Tab-uri -->
    <v-tabs v-model="activeTab" class="inl-tabs" color="#ff5722" density="comfortable">
      <v-tab value="concedii">
        <v-icon size="18" class="mr-2">mdi-calendar-account</v-icon>
        Concedii ({{ cereri.length }})
      </v-tab>
      <v-tab value="tichete">
        <v-icon size="18" class="mr-2">mdi-ticket-confirmation-outline</v-icon>
        Tichete ({{ tichete.length }})
      </v-tab>
    </v-tabs>

    <!-- Tab Concedii -->
    <div v-show="activeTab === 'concedii'" class="grid-card">
      <div class="grid-head">
        <v-icon size="18" color="#ff5722" class="mr-2">mdi-account-switch-outline</v-icon>
        <span>Cereri de concediu care așteaptă confirmarea ta</span>
      </div>
      <DxDataGrid
        :data-source="cereri"
        key-expr="ID"
        :show-borders="false"
        :show-column-lines="false"
        :show-row-lines="true"
        :row-alternation-enabled="false"
        :hover-state-enabled="true"
        :no-data-text="loading ? 'Se încarcă…' : 'Nicio solicitare de înlocuire.'"
      >
        <DxColumn data-field="NumeAngajat" caption="Solicitant" :min-width="180" cell-template="angCell" />
        <DxColumn data-field="TipConcediu" caption="Tip" :width="130" cell-template="tipCell" />
        <DxColumn data-field="PerioadaText" caption="Perioadă" :width="210" cell-template="perioadaCell" :allow-filtering="false" />
        <DxColumn data-field="NumarZile" caption="Zile" :width="80" alignment="center" cell-template="zileCell" />
        <DxColumn data-field="Descriere" caption="Descriere" :min-width="160" />
        <DxColumn caption="" :width="140" :allow-filtering="false" :allow-sorting="false" cell-template="actiuniCell" />

        <template #angCell="{ data }">
          <div class="ang">
            <span class="avatar">{{ data.data.Initiale }}</span>
            <span class="ang-nume">{{ data.data.NumeAngajat || 'Coleg' }}</span>
          </div>
        </template>
        <template #tipCell="{ data }">
          <span class="tip-badge">{{ data.data.TipConcediu || 'CO' }}</span>
        </template>
        <template #perioadaCell="{ data }">
          <div class="perioada"><v-icon size="15" color="#ff5722" class="mr-1">mdi-calendar-range</v-icon>{{ data.data.PerioadaText }}</div>
        </template>
        <template #zileCell="{ data }">
          <span class="zile-badge">{{ data.data.NumarZile }}</span>
        </template>
        <template #actiuniCell="{ data }">
          <v-btn size="small" class="btn-confirma" prepend-icon="mdi-check-circle" @click="openConfirm(data.data)">Confirmă</v-btn>
        </template>

        <DxPaging :page-size="12" />
        <DxSorting mode="single" />
        <DxFilterRow :visible="true" />
      </DxDataGrid>
    </div>

    <!-- Tab Tichete (read-only) -->
    <div v-show="activeTab === 'tichete'" class="grid-card">
      <div class="grid-head">
        <v-icon size="18" color="#ff5722" class="mr-2">mdi-ticket-confirmation-outline</v-icon>
        <span>Tichete în care ești desemnat înlocuitor</span>
      </div>
      <DxDataGrid
        :data-source="tichete"
        key-expr="ID"
        :show-borders="false"
        :show-column-lines="false"
        :show-row-lines="true"
        :row-alternation-enabled="false"
        :hover-state-enabled="true"
        :no-data-text="loading ? 'Se încarcă…' : 'Niciun tichet care să te aibă ca înlocuitor.'"
      >
        <DxColumn data-field="NumeAngajat" caption="Solicitant" :min-width="180" cell-template="tAngCell" />
        <DxColumn data-field="TipCerere" caption="Tip cerere" :width="150" cell-template="tTipCell" />
        <DxColumn data-field="PerioadaText" caption="Perioadă" :width="210" cell-template="tPerioadaCell" :allow-filtering="false" />
        <DxColumn data-field="ZileText" caption="Zile / Durată" :width="120" alignment="center" cell-template="tZileCell" :allow-filtering="false" />
        <DxColumn data-field="DestinatieText" caption="Destinație" :min-width="150" />
        <DxColumn data-field="Explicatie" caption="Explicație" :min-width="180" />

        <template #tAngCell="{ data }">
          <div class="ang">
            <span class="avatar">{{ data.data.Initiale }}</span>
            <span class="ang-nume">{{ data.data.NumeAngajat || 'Coleg' }}</span>
          </div>
        </template>
        <template #tTipCell="{ data }">
          <span class="tip-badge tip-badge-tichet">{{ data.data.TipCerere || 'Tichet' }}</span>
        </template>
        <template #tPerioadaCell="{ data }">
          <div class="perioada"><v-icon size="15" color="#ff5722" class="mr-1">mdi-calendar-range</v-icon>{{ data.data.PerioadaText }}</div>
        </template>
        <template #tZileCell="{ data }">
          <span class="zile-badge">{{ data.data.ZileText }}</span>
        </template>

        <DxPaging :page-size="12" />
        <DxSorting mode="single" />
        <DxFilterRow :visible="true" />
      </DxDataGrid>
    </div>

    <!-- Dialog confirmare -->
    <v-dialog v-model="dialog" max-width="480">
      <v-card rounded="xl" class="dlg-card">
        <div class="dlg-head">
          <div class="dlg-ic"><v-icon color="#fff" size="20">mdi-account-check-outline</v-icon></div>
          <div class="dlg-txt">
            <div class="dlg-title">Confirmă înlocuirea</div>
            <div class="dlg-desc">Cererea va merge mai departe la superior.</div>
          </div>
          <v-btn icon="mdi-close" variant="text" color="#fff" size="small" @click="dialog = false" />
        </div>
        <v-card-text class="pa-5" v-if="selectat">
          <div class="rez">
            <div class="rez-row"><span class="rez-lbl">Solicitant</span><span class="rez-val">{{ selectat.NumeAngajat }}</span></div>
            <div class="rez-row"><span class="rez-lbl">Tip</span><span class="rez-val">{{ selectat.TipConcediu || 'CO' }}</span></div>
            <div class="rez-row"><span class="rez-lbl">Perioadă</span><span class="rez-val">{{ selectat.PerioadaText }}</span></div>
            <div class="rez-row"><span class="rez-lbl">Zile</span><span class="rez-val">{{ selectat.NumarZile }}</span></div>
            <div v-if="selectat.Descriere" class="rez-row"><span class="rez-lbl">Descriere</span><span class="rez-val">{{ selectat.Descriere }}</span></div>
          </div>
          <v-textarea v-model="observatie" label="Observație / explicație (opțional)" variant="outlined" density="comfortable" rows="2" hide-details class="mt-4" />
        </v-card-text>
        <v-card-actions class="dlg-foot">
          <v-btn variant="text" @click="dialog = false">Anulează</v-btn>
          <v-spacer />
          <v-btn class="btn-confirma px-6" :loading="confirmand" @click="confirma">
            <v-icon start>mdi-check-circle</v-icon>Confirmă
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { DxDataGrid, DxColumn, DxPaging, DxFilterRow, DxSorting } from 'devextreme-vue/data-grid'
import notify from 'devextreme/ui/notify'
import PORTAL_Aprobari from '@/services/Portal/PORTAL_Aprobari'

export default defineComponent({
  name: 'SolicitariInlocuire',
  components: { DxDataGrid, DxColumn, DxPaging, DxFilterRow, DxSorting },
  data() {
    return {
      loading: true,
      activeTab: 'concedii' as 'concedii' | 'tichete',
      cereri: [] as any[],
      tichete: [] as any[],
      dialog: false,
      selectat: null as any,
      observatie: '',
      confirmand: false,
    }
  },
  mounted() {
    this.incarca();
  },
  methods: {
    fmt(d: any) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    initiale(nume?: string) {
      if (!nume) return '?';
      return nume.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
    },
    // Formatare dată din valoare care poate fi string liber (view-ul de tichete are DataStart/Stop ca text).
    fmtLoose(d: any) {
      if (!d) return '';
      const parsed = new Date(d);
      if (isNaN(parsed.getTime())) return String(d);
      return parsed.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    async incarca() {
      this.loading = true;
      try {
        const [respConcedii, respTichete] = await Promise.all([
          PORTAL_Aprobari.getInlocuitorPending().catch(() => null),
          PORTAL_Aprobari.getInlocuitorPendingTichete().catch(() => null),
        ]);

        const lista = respConcedii?.data?.Cereri ?? [];
        // Precalculăm câmpurile de afișaj (fără apeluri de funcție în cell-template).
        this.cereri = lista.map((c: any) => ({
          ...c,
          Initiale: this.initiale(c.NumeAngajat),
          PerioadaText: `${this.fmt(c.DataInceput)} → ${this.fmt(c.DataSfarsit)}`,
        }));

        const listaT = respTichete?.data?.Tichete ?? [];
        this.tichete = listaT.map((t: any) => {
          const start = this.fmtLoose(t.DataStart);
          const stop = this.fmtLoose(t.DataStop);
          const perioada = stop && stop !== start ? `${start} → ${stop}` : (start || '—');
          const zile = t.DurataZile || t.NumarZile || t.Durata || t.NumarOre || '—';
          const destinatie = t.Destinatie || t.Locatie || '';
          return {
            ...t,
            Initiale: this.initiale(t.NumeAngajat),
            PerioadaText: perioada,
            ZileText: zile,
            DestinatieText: destinatie,
          };
        });
      } catch {
        this.cereri = [];
        this.tichete = [];
      } finally {
        this.loading = false;
      }
    },
    openConfirm(c: any) {
      this.selectat = c;
      this.observatie = '';
      this.dialog = true;
    },
    async confirma() {
      if (!this.selectat) return;
      this.confirmand = true;
      try {
        await PORTAL_Aprobari.confirmaInlocuitor(this.selectat.ID, this.observatie);
        notify('Ai confirmat înlocuirea. Cererea a fost trimisă superiorului.', 'success', 3500);
        this.dialog = false;
        await this.incarca();
      } catch {
        notify('Nu s-a putut confirma solicitarea. Încearcă din nou.', 'error', 4000);
      } finally {
        this.confirmand = false;
      }
    },
  }
})
</script>

<style scoped>
.inl-page { padding: 16px; width: 100%; }

.hero {
  display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;
  background: linear-gradient(120deg, #ff5722 0%, #ff7a45 55%, #ff9a5c 100%);
  border-radius: 18px; padding: 22px 26px; color: #fff;
  box-shadow: 0 10px 30px rgba(255, 87, 34, .28); margin-bottom: 18px;
}
.hero-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 2px; opacity: .85; }
.hero-title { font-size: 23px; font-weight: 800; line-height: 1.2; margin: 4px 0 2px; }
.hero-sub { font-size: 13px; opacity: .92; margin: 0; max-width: 640px; }
.co-chip { display: flex; align-items: center; gap: 5px; background: rgba(255, 255, 255, .22); padding: 9px 15px; border-radius: 22px; }
.co-val { font-size: 18px; font-weight: 800; }
.co-lbl { font-size: 11px; font-weight: 600; opacity: .92; }

.grid-card { background: #fff; border: 1px solid #eef1f6; border-radius: 16px; box-shadow: 0 1px 3px rgba(15,23,42,.05); overflow: hidden; }
.grid-head { display: flex; align-items: center; padding: 15px 20px; border-bottom: 1px solid #f1f5f9; font-size: 14px; font-weight: 700; color: #1e293b; }

.ang { display: flex; align-items: center; gap: 10px; }
.avatar { width: 32px; height: 32px; border-radius: 9px; flex: 0 0 32px; background: #fff1ec; color: #ff5722; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 12px; }
.ang-nume { font-weight: 700; color: #1e293b; }
.tip-badge { display: inline-block; padding: 3px 10px; border-radius: 8px; background: #f5f3ff; color: #8b5cf6; font-weight: 700; font-size: 12px; }
.tip-badge-tichet { background: #fff1ec; color: #ff5722; }
.inl-tabs { margin-bottom: 14px; }
.perioada { display: flex; align-items: center; font-weight: 600; color: #1e293b; }
.zile-badge { display: inline-block; min-width: 28px; padding: 3px 8px; border-radius: 8px; background: #fff1ec; color: #ff5722; font-weight: 800; font-size: 13px; text-align: center; }
.btn-confirma { background: #ff5722 !important; color: #fff !important; font-weight: 700; border-radius: 10px; text-transform: none; letter-spacing: 0; box-shadow: none; }

.dlg-card { overflow: hidden; }
.dlg-head { display: flex; align-items: center; gap: 12px; background: linear-gradient(120deg,#ff5722,#ff8a5c); color: #fff; padding: 16px 18px; }
.dlg-ic { width: 40px; height: 40px; border-radius: 12px; background: rgba(255,255,255,.2); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.dlg-title { font-size: 16px; font-weight: 800; line-height: 1.15; }
.dlg-desc { font-size: 12px; opacity: .9; margin-top: 2px; }
.rez { background: #fafbfd; border: 1px solid #eef1f6; border-radius: 12px; padding: 6px 14px; }
.rez-row { display: flex; justify-content: space-between; gap: 12px; padding: 8px 0; border-bottom: 1px solid #f1f5f9; }
.rez-row:last-child { border-bottom: none; }
.rez-lbl { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .4px; color: #94a3b8; }
.rez-val { font-size: 13.5px; font-weight: 600; color: #1e293b; text-align: right; }
.dlg-foot { padding: 14px 20px; border-top: 1px solid #eef1f6; }
</style>
